import * as React from "react";
import { Button, Input } from "@jhool-io/fe-components";
import SignaturePad from "signature_pad";

interface ConsentSignatureInputProps {
    value: string;
    onChange: (value: string) => void;
    onInteract: () => void;
    hideModeToggle?: boolean;
    label?: string;
}

function renderTypedSignatureDataUrl(text: string): string {
    const canvas = document.createElement("canvas");
    canvas.width = 400;
    canvas.height = 160;
    const context = canvas.getContext("2d");
    if (!context) {
        return "";
    }

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.fillStyle = "#0B132B";
    context.font = "italic 44px Georgia, 'Times New Roman', serif";
    context.textBaseline = "middle";
    context.textAlign = "center";
    context.fillText(text, canvas.width / 2, canvas.height / 2);
    return canvas.toDataURL("image/png");
}

export default function ConsentSignatureInput({
    value,
    onChange,
    onInteract,
    hideModeToggle = false,
    label = "Signature",
}: ConsentSignatureInputProps) {
    const [mode, setMode] = React.useState<"draw" | "type">("draw");
    const [typedSignature, setTypedSignature] = React.useState("");
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const signaturePadRef = React.useRef<SignaturePad | null>(null);
    const lastDrawnValueRef = React.useRef("");
    const onChangeRef = React.useRef(onChange);
    const onInteractRef = React.useRef(onInteract);
    onChangeRef.current = onChange;
    onInteractRef.current = onInteract;

    React.useEffect(() => {
        const canvas = canvasRef.current;
        if (mode !== "draw" || !canvas) {
            return undefined;
        }

        canvas.width = Math.max(
            Math.floor(canvas.getBoundingClientRect().width),
            1
        );
        canvas.height = 140;

        const signaturePad = new SignaturePad(canvas, {
            penColor: "#0B132B",
        });
        const handleEndStroke = () => {
            if (!signaturePad.isEmpty()) {
                const nextValue = signaturePad.toDataURL("image/png");
                lastDrawnValueRef.current = nextValue;
                onChangeRef.current(nextValue);
                onInteractRef.current();
            }
        };

        signaturePad.addEventListener("endStroke", handleEndStroke);
        signaturePadRef.current = signaturePad;

        return () => {
            signaturePad.removeEventListener("endStroke", handleEndStroke);
            signaturePad.off();
            signaturePadRef.current = null;
        };
    }, [mode]);

    React.useEffect(() => {
        const signaturePad = signaturePadRef.current;
        if (mode !== "draw" || !signaturePad) {
            return;
        }

        if (value === lastDrawnValueRef.current) {
            lastDrawnValueRef.current = "";
            return;
        }

        if (value) {
            signaturePad.fromDataURL(value);
        } else {
            signaturePad.clear();
        }
    }, [mode, value]);

    const changeMode = (nextMode: "draw" | "type") => {
        setMode(nextMode);
        setTypedSignature("");
        signaturePadRef.current?.clear();
        onChange("");
        onInteract();
    };

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <p className="text-[10px] uppercase tracking-wide text-[rgba(11,19,43,0.4)]">
                    * {label}
                </p>
                {!hideModeToggle && (
                    <div className="flex bg-[rgba(11,19,43,0.06)] rounded-md p-0.5">
                        {(["draw", "type"] as const).map((signatureMode) => (
                            <Button
                                key={signatureMode}
                                type="button"
                                variant="normal"
                                size="auto"
                                onClick={() => changeMode(signatureMode)}
                                className={`text-xs px-3 py-1 rounded capitalize transition-colors ${
                                    mode === signatureMode
                                        ? "bg-white text-[#0B132B] shadow-sm font-medium"
                                        : "text-[rgba(11,19,43,0.5)]"
                                }`}
                            >
                                {signatureMode}
                            </Button>
                        ))}
                    </div>
                )}
            </div>

            {mode === "draw" ? (
                <>
                    <div className="border border-stroke rounded-md overflow-hidden bg-white">
                        <canvas
                            ref={canvasRef}
                            aria-label="Draw signature"
                            className="block h-[140px] w-full touch-none cursor-crosshair"
                            height={140}
                        />
                    </div>
                    <Button
                        variant="normal"
                        onClick={() => {
                            signaturePadRef.current?.clear();
                            onChange("");
                            onInteract();
                        }}
                        className="text-xs text-[rgba(11,19,43,0.4)] underline mt-4"
                    >
                        Clear signature
                    </Button>
                </>
            ) : (
                <>
                    <Input
                        label=""
                        type="text"
                        value={typedSignature}
                        onChange={(event) => {
                            const nextValue = event.target.value;
                            setTypedSignature(nextValue);
                            onChange(
                                nextValue.trim()
                                    ? renderTypedSignatureDataUrl(
                                          nextValue.trim()
                                      )
                                    : ""
                            );
                            onInteract();
                        }}
                        placeholder="Type your full name"
                    />
                    {typedSignature.trim() && (
                        <div className="mt-3 border border-stroke rounded-md bg-white flex items-center justify-center h-[80px] px-4">
                            <span
                                style={{
                                    fontFamily:
                                        "Georgia, 'Times New Roman', serif",
                                    fontStyle: "italic",
                                    fontSize: "2rem",
                                    color: "#0B132B",
                                }}
                            >
                                {typedSignature}
                            </span>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
